import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslocoModule } from '@jsverse/transloco';
import { AssetUrlPipe } from '@educational-initiatives/angular-web-utilities';
import type { NavItem } from '@app/shared/types';
import { DEFAULT_LAYOUT_VALUES } from '@app/shared/constants';
import {
  LayoutIconComponent,
  AppPaletteComponent,
  UserProfileComponent,
} from '@app/shared/components';
import { AuthService } from '@educational-initiatives/angular-web-utilities';
import { AuthFlowService } from '@app/core/services/auth-flow/auth-flow.service';

/**
 * @fileoverview HeaderComponent
 * @description
 * **The Crown**
 * The primary header component providing branding (logo, app name) and global
 * controls (app palette, user profile, logout). On desktop, includes a
 * hamburger to open the sidebar; on mobile, navigation is via the bottom nav.
 *
 * ARCHITECTURAL PRINCIPLES:
 * 1. Minimal header: logo + actions
 * 2. Desktop: hamburger triggers sidebar (handled by parent)
 * 3. Mobile: no hamburger (bottom nav has "More options")
 * 4. Logout is always visible next to the profile control
 */
@Component({
  selector: 'app-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    AssetUrlPipe,
    TranslocoModule,
    LayoutIconComponent,
    AppPaletteComponent,
    UserProfileComponent,
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
})
export class HeaderComponent implements OnInit {
  private readonly _authService = inject(AuthService);
  private readonly _authFlow = inject(AuthFlowService);

  /** The name of the application displayed in the header */
  public readonly appName = input(DEFAULT_LAYOUT_VALUES.APP_NAME);

  /** The list of navigation items (kept for compatibility; nav is in sidebar) */
  public readonly navItems = input<NavItem[]>([]);

  /** Whether the sidebar is open (used for panel icon state) */
  public readonly sidebarOpen = input<boolean>(false);

  /**
   * When false, the desktop sidebar toggle is hidden (e.g. missing `show_side_bar` permission).
   * Defaults to true for standalone/header tests; main layout binds from permissions.
   */
  public readonly sidebarEnabled = input(true);

  /**
   * Route for "Manage your account" in the profile menu. Omitted when the user lacks
   * `show_manage_accounts` (main layout passes undefined).
   */
  public readonly manageAccountUrl = input<string | undefined>(undefined);

  /** Emitted when hamburger is clicked to toggle sidebar */
  public readonly menuToggle = output<void>();

  /** Logo filename for AssetUrlPipe (mobile only) */
  protected readonly _logoFilename = 'ei_educational_initiatives.png';

  /** User info signal; truthy when authenticated */
  protected readonly _userInfo = this._authService.userInfo;

  protected readonly _isLoggingOut = signal(false);

  public ngOnInit(): void {
    this._authService.fetchUserInfo().subscribe({ error: () => {} });
  }

  protected _onMenuToggle(): void {
    this.menuToggle.emit();
  }

  protected _onLogout(): void {
    if (this._isLoggingOut()) {
      return;
    }
    this._isLoggingOut.set(true);
    void this._authFlow.logout().catch(() => {
      this._isLoggingOut.set(false);
    });
  }
}
